import { useCallback, useMemo } from 'react';
import { isEmpty, compose, sortWith, ascend, uniq } from 'ramda';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import { useFeature } from '@growthbook/growthbook-react';
import useRequestConfig from './useRequestConfig';
import { getAPI } from '../../services/API';
import { getBonusContentEntity } from '../../utils/bonus';
import { getEntityWithImageUrl } from '../../utils/imagePath';
import {
  brandSelector,
  channelSelector,
  languageSelector,
  isLoggedInSelector,
} from '../../redux/reducers/app/params';
import { contentAPIPrefixSelector } from '../../redux/reducers/app/configuration';
import {
  QUERY,
  ENTITY,
  PROJECT_KEY,
  CHANNEL_CONFIG_MAP,
} from '../../constants/content';
import {
  NOT_AVAILABLE,
  CASHBACK_TRIGGER_TYPE,
  FREE_SPINS_FOR_JOIN_TRIGGER_TYPE,
  FREE_SPINS_FOR_DEPOSIT_TRIGGER_TYPE,
  WAGERING_BONUS_MECHANIC,
  PENDING,
  WAGERING,
  COLLECTING,
  CASHBACK_BONUS_TYPE,
  FREE_SPINS_BONUS_TYPE,
  FREE_SPINS_BONUS_MECHANIC,
  CONSUMED,
  CONSUMED_PENDING,
} from '../../constants/bonuses';
import { FEATURES } from '../../constants/featureToggling';
import { sdkErrorHandler } from '../../utils';
import { useIsUnifiedPromoBonusSystemEnabled } from '../useIsUnifiedPromoBonusSystemEnabled';

export const useFetchBonusContent = (ids = []) => {
  const brand = useSelector(brandSelector);
  const language = useSelector(languageSelector);
  const channel = useSelector(channelSelector);
  const contentAPIPrefix = useSelector(contentAPIPrefixSelector);

  const query = {
    [QUERY.PROJECT]: PROJECT_KEY,
    [QUERY.LANGUAGE]: language,
    [QUERY.BRANDS]: brand.toUpperCase(),
    [QUERY.CHANNELS]: CHANNEL_CONFIG_MAP[channel.toUpperCase()],
  };
  const searchParams = new URLSearchParams(query);

  if (Array.isArray(ids) && !isEmpty(ids)) {
    // use sort to cache request with CF
    const sortedIds = sortWith([ascend((a) => a)])(ids);

    sortedIds.forEach((id) => {
      if (!id || typeof id !== 'string') {
        return;
      }

      searchParams.append(QUERY.EXTERNAL_ID_IN, id);
    });
  }

  const url = `/${ENTITY.BONUSES}?${searchParams}`;

  return ({ signal }) =>
    getAPI()
      .fetch(url, { baseUrl: contentAPIPrefix, signal })
      .catch(
        sdkErrorHandler({
          source: `fetch to ${url}`,
          baseUrl: contentAPIPrefix,
        })
      );
};

export const useBonusContentSelector = () => {
  const contentAPIPrefix = useSelector(contentAPIPrefixSelector);

  return useCallback(
    (data) => {
      return data
        .map((item) => {
          return compose(
            (entity) => getBonusContentEntity(entity, contentAPIPrefix),
            getEntityWithImageUrl
          )(item, contentAPIPrefix);
        })
        .reduce((acc, item) => ({ ...acc, [item.id]: item }), {});
    },
    [contentAPIPrefix]
  );
};

/**
 * Queries of bonus data
 */

export const QUERY_KEY_BONUSES = 'bonuses';
export const QUERY_KEY_BONUS_HISTORY = 'bonus-history';
export const QUERY_KEY_CAMPAIGN = 'campaign';
export const QUERY_KEY_BONUS_CONTENT = 'bonus-content';

export const useQueryBonusContent = (ids = [], queryOptions = {}) => {
  const fetch = useFetchBonusContent(ids);
  const dataSelector = useBonusContentSelector();

  return useQuery([QUERY_KEY_BONUS_CONTENT, ids], fetch, {
    ...queryOptions,
    select: dataSelector,
  });
};

export const useQueryBonusSystemFn = () => {
  const requestConfig = useRequestConfig();
  const isPromoSystemEnabled = useIsUnifiedPromoBonusSystemEnabled();

  const query = {
    ...(requestConfig?.query || {}),
    ...(isPromoSystemEnabled ? { isUnifiedLobby: true } : {}),
  };

  return ({ signal }) =>
    getAPI()
      .bonuses.get({
        signal,
        apiVersion: 'v3',
        ...requestConfig,
        query,
      })
      .catch(
        sdkErrorHandler({
          source: 'API SDK bonuses.get',
          apiVersion: 'v3',
          ...requestConfig,
        })
      );
};

export const useQueryBonusSystem = (queryOptions = {}) => {
  const fetch = useQueryBonusSystemFn();

  return useQuery(QUERY_KEY_BONUSES, fetch, {
    staleTime: 0,
    cacheTime: 0,
    ...queryOptions,
  });
};

export const useQueryBonusSystemHistory = (queryOptions = {}) => {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const requestConfig = useRequestConfig();

  const fetch = ({ signal }) =>
    getAPI()
      .bonusHistory.get({ signal, apiVersion: 'v3', ...requestConfig })
      .catch(
        sdkErrorHandler({
          source: 'API SDK bonusHistory.get',
          apiVersion: 'v3',
          ...requestConfig,
        })
      );

  return useQuery(QUERY_KEY_BONUS_HISTORY, fetch, {
    staleTime: 0,
    cacheTime: 0,
    enabled: isLoggedIn,
    ...queryOptions,
  });
};

export const useQueryCampaign = (campaignId, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const fetch = ({ signal }) =>
    getAPI()
      .bonuses.getCampaign(campaignId, {
        ...requestConfig,
        apiVersion: 'v3',
      })
      .catch(
        sdkErrorHandler({
          source: 'API SDK bonuses.getCampaign',
          apiVersion: 'v3',
          campaignId,
          ...requestConfig,
        })
      );

  return useQuery(QUERY_KEY_CAMPAIGN, fetch, {
    staleTime: 0,
    cacheTime: 0,
    enabled: !!campaignId,
    ...queryOptions,
  });
};

/**
 * High level queries of bonuses
 */

export const getCampaignList = (campaigns) => {
  return Object.values(campaigns)
    .flat()
    .sort((a, b) => {
      return Date.parse(b.startTime) - Date.parse(a.startTime);
    });
};

export const getBonusesList = ({ bonuses, isActive = false }) => {
  return Object.entries(bonuses)
    .map(([mechanicType, bonuses]) => {
      return bonuses.map((bonus) => ({
        ...bonus,
        mechanicType,
        // active bonus can not be in CONSUMED status. This is transitional state that is handled on FE side
        status:
          isActive && bonus.status === CONSUMED
            ? CONSUMED_PENDING
            : bonus.status,
      }));
    })
    .flat()
    .sort((a, b) => {
      return Date.parse(b.lastUpdatedTime) - Date.parse(a.lastUpdatedTime);
    });
};

export const getAvailableEntity = (entities, entityId, isCampaign = true) => {
  const entity = entities.find((item) => item.id === entityId);

  if (isCampaign && !entity) {
    // Return bonus with campaign ID as fallback
    return entities.find((item) => item.campaignId === entityId);
  }

  return entity;
};

export const getUnavailableEntity = (entity, content) => {
  if (!content && !entity) return null;

  return {
    ...entity,
    ...content,
    isCampaign: true,
    status: NOT_AVAILABLE,
    campaignTriggerType: entity?.triggerDetails?.type,
  };
};

export const getUniqCampaignIds = (bonuses = [], campaigns = []) => {
  const campaignIds = campaigns.map((campaign) => campaign?.id);
  const campaignIdsFromBonuses = bonuses.map((bonus) => bonus?.campaignId);

  return uniq([...campaignIds, ...campaignIdsFromBonuses]) || [];
};

export const useBonusesData = ({
  campaigns = [],
  bonuses = [],
  contentByCampaignId = {},
}) => {
  const isCashbackBonusEnabled = useFeature(FEATURES.CAN_USE_CASHBACK_BONUS).on;

  const isFreeSpinsBonusEnabled = useFeature(FEATURES.CAN_USE_FREE_SPINS).on;

  const bonusesUIEntities = bonuses.map((bonus) => ({
    ...(contentByCampaignId[bonus.campaignId] || {}),
    ...bonus,
    id: bonus.id,
    isCampaign: false,
  }));
  const campaignsUIEntities = campaigns.map((campaign) => ({
    ...(contentByCampaignId[campaign.id] || {}),
    ...campaign,
    id: campaign.id,
    isCampaign: true,
    campaignTriggerType: campaign?.triggerDetails?.type,
  }));

  const filteredCampaigns = campaignsUIEntities.filter((campaign) => {
    if (
      !isCashbackBonusEnabled &&
      campaign.campaignTriggerType === CASHBACK_TRIGGER_TYPE
    ) {
      return false;
    }

    if (
      !isFreeSpinsBonusEnabled &&
      [
        FREE_SPINS_FOR_JOIN_TRIGGER_TYPE,
        FREE_SPINS_FOR_DEPOSIT_TRIGGER_TYPE,
      ].includes(campaign.campaignTriggerType)
    ) {
      return false;
    }

    return true;
  });

  return [...bonusesUIEntities, ...filteredCampaigns];
};

export const useQueryBonuses = (queryOptions = {}) => {
  const queryBonuses = useQueryBonusSystem(queryOptions);

  const bonuses = getBonusesList({
    bonuses: queryBonuses.data?.bonuses ?? {},
    isActive: true,
  });
  const campaigns = getCampaignList(queryBonuses.data?.campaigns ?? {});
  const campaignIds = getUniqCampaignIds(bonuses, campaigns);

  const isEnabledBonusesContentQuery = !isEmpty(campaignIds);

  const queryBonusesContent = useQueryBonusContent(campaignIds, {
    enabled: isEnabledBonusesContentQuery,
  });

  const contentByCampaignId = queryBonusesContent.data ?? {};

  const data = useBonusesData({
    campaigns,
    bonuses,
    contentByCampaignId,
  });

  return {
    ...queryBonuses,
    data,
    isSuccess:
      queryBonuses.isSuccess &&
      (isEnabledBonusesContentQuery ? queryBonusesContent.isSuccess : true),
    isLoading:
      queryBonuses.isLoading ||
      (isEnabledBonusesContentQuery ? queryBonusesContent.isLoading : false),
  };
};

export const useQueryBonusHistory = (queryOptions = {}) => {
  const queryBonuses = useQueryBonusSystemHistory(queryOptions);

  const bonuses = getBonusesList({
    bonuses: queryBonuses.data ?? {},
    isActive: false,
  });
  const campaignIds = getUniqCampaignIds(bonuses);

  const queryBonusesContent = useQueryBonusContent(campaignIds, {
    enabled: !isEmpty(campaignIds),
  });

  const contentByCampaignId = queryBonusesContent.data ?? {};

  const data = useBonusesData({
    bonuses,
    contentByCampaignId,
  });

  return { ...queryBonuses, data };
};

export const useQueryUnavailableCampaign = (campaignId, queryOptions = {}) => {
  const queryContent = useQueryBonusContent([campaignId], queryOptions);
  const queryCampaign = useQueryCampaign(campaignId, queryOptions);

  const data = getUnavailableEntity(
    queryCampaign.data?.campaign,
    queryContent.data?.[campaignId]
  );

  return {
    isLoading: queryContent.isLoading || queryCampaign.isLoading,
    isError: queryContent.isError || queryCampaign.isError,
    error: queryContent.error || queryCampaign.error,
    data,
  };
};

export const useQueryBonusDetails = (
  entityId,
  isCampaign = true,
  queryOptions = {}
) => {
  const queryBonuses = useQueryBonuses(queryOptions);
  const queryHistory = useQueryBonusHistory(queryOptions);

  const entities = [...queryBonuses.data, ...queryHistory.data];
  const availableEntity = getAvailableEntity(entities, entityId, isCampaign);

  const isEntityIsNotAvailable =
    !queryBonuses.isLoading && !queryHistory.isLoading && !availableEntity;

  const queryUnavailableCampaign = useQueryUnavailableCampaign(entityId, {
    enabled: isEntityIsNotAvailable,
  });

  const data = isEntityIsNotAvailable
    ? queryUnavailableCampaign?.data
    : availableEntity;

  return {
    isLoading:
      queryBonuses.isLoading ||
      queryHistory.isLoading ||
      queryUnavailableCampaign.isLoading,
    isError:
      queryBonuses.isError ||
      queryHistory.isError ||
      queryUnavailableCampaign.isError,
    error:
      queryBonuses.error ||
      queryHistory.error ||
      queryUnavailableCampaign.error,
    data,
  };
};

/**
 * Mutations of bonus
 */

export const useMutationJoinCampaign = (campaignId, mutationOptions = {}) => {
  const requestConfig = useRequestConfig();

  return useMutation(
    () =>
      getAPI().bonuses.join(campaignId, { ...requestConfig, apiVersion: 'v3' }),
    mutationOptions
  );
};

export const useMutationUnJoinCampaign = (campaignId, mutationOptions = {}) => {
  const requestConfig = useRequestConfig();

  return useMutation(
    () =>
      getAPI().bonuses.unjoin(campaignId, {
        apiVersion: 'v3',
        ...requestConfig,
      }),
    mutationOptions
  );
};

export const useMutationRejectBonus = (
  bonusId,
  campaignId,
  mutationOptions = {}
) => {
  const requestConfig = useRequestConfig();

  return useMutation(
    () =>
      getAPI().bonuses.reject(
        { bonusId, campaignId },
        { apiVersion: 'v3', ...requestConfig }
      ),
    mutationOptions
  );
};

export const useMutationTakeCashback = (
  bonusId,
  campaignId,
  mutationOptions = {}
) => {
  const requestConfig = useRequestConfig();

  const fetch = () =>
    getAPI().bonuses.take(
      { bonusId, campaignId },
      { apiVersion: 'v3', ...requestConfig }
    );

  return useMutation(fetch, mutationOptions);
};

/**
 * Common hooks
 */

export const useWageringBonus = (queryOptions = {}) => {
  const { data, isLoading } = useQueryBonuses(queryOptions);

  const wageringBonuses = useMemo(
    () =>
      data.filter(
        (bonus) =>
          !bonus.isCampaign &&
          [PENDING, WAGERING].includes(bonus.status) &&
          bonus.mechanicType === WAGERING_BONUS_MECHANIC
      ),
    [data]
  );

  const firstWageringBonus = useMemo(
    () =>
      wageringBonuses.reduce((acc, el) => {
        if (!acc) return el;
        return new Date(el?.startTime) - new Date(acc?.startTime) > 0
          ? acc
          : el;
      }, null),
    [wageringBonuses]
  );

  return useMemo(() => {
    return {
      data: firstWageringBonus,
      isLoading,
    };
  }, [firstWageringBonus, isLoading]);
};

export const useWageringGamesCampaignId = () => {
  const { data, isLoading } = useWageringBonus();
  const campaignId = data?.campaignId;

  return useMemo(() => {
    return {
      isLoading,
      campaignId,
    };
  }, [campaignId, isLoading]);
};

export const useCollectingGamesCampaignId = () => {
  const { data, isLoading } = useQueryBonuses();

  return useMemo(() => {
    const collectingBonus = data.find(
      (bonus) =>
        bonus.status === COLLECTING &&
        bonus.bonusType === CASHBACK_BONUS_TYPE &&
        bonus.mechanicType !== FREE_SPINS_BONUS_MECHANIC
    );
    const campaignId = collectingBonus?.campaignId;

    return {
      isLoading,
      campaignId,
    };
  }, [data, isLoading]);
};

export const useIsActiveCashbackBonusExists = () => {
  const { data } = useQueryBonuses();

  return useMemo(
    () =>
      data.some(
        (bonus) => !bonus.isCampaign && bonus.bonusType === CASHBACK_BONUS_TYPE
      ),
    [data]
  );
};

export const useIsActiveFreeSpinsBonusExists = () => {
  const { data } = useQueryBonuses();

  return useMemo(
    () =>
      data.some(
        (bonus) =>
          !bonus.isCampaign && bonus.bonusType === FREE_SPINS_BONUS_TYPE
      ),
    [data]
  );
};

export const useBonusesDataSelector = (selector) => {
  const { data, ...query } = useQueryBonuses();

  if (!selector) {
    throw new Error('selector is required');
  }

  return useMemo(() => {
    return selector(data, query);
  }, [data, query, selector]);
};
